import { CHROMATIC_SHARP, CHROMATIC_FLAT, SHARP_KEYS } from '../data/notes';
import { ScaleQuality, ChordDegree, ChordFunction, DiatonicChord, Key } from '../data/keys';
import { ChordQuality, buildChord, CHORD_VARIANTS } from '../data/chords';
import {
  Mode, MODE_INTERVALS, MODE_DIATONIC_QUALITIES,
  MODE_ROMAN_NUMERALS, MODE_CHORD_FUNCTIONS, PARENT_OFFSET,
} from '../data/modes';
import { Progression, ProgressionStep } from '../data/progressions';

const MODE_TO_QUALITY: Record<Mode, ScaleQuality> = {
  ionian: 'major', lydian: 'major', mixolydian: 'major',
  dorian: 'minor', aeolian: 'minor', phrygian: 'minor', locrian: 'minor',
};

// Resolve the parent-major root for any (mode tonic, mode) pair.
// e.g. parentMajorRoot('D', 'dorian') === 'C'  (D Dorian belongs to C major)
function parentMajorRoot(root: string, mode: Mode): string {
  const offset = PARENT_OFFSET[mode];
  // Try the sharp spelling first; if it resolves to a sharp-using key root, use it.
  // Otherwise fall back to the flat spelling.
  const sharpVer = transposeNote(root, offset, true);
  if (SHARP_KEYS.has(sharpVer)) return sharpVer;
  return transposeNote(root, offset, false);
}

function usesSharpsForMode(root: string, mode: Mode): boolean {
  const parent = parentMajorRoot(root, mode);
  return SHARP_KEYS.has(parent);
}

export function transposeNote(note: string, semitones: number, useSharps?: boolean): string {
  const normalized = note.replace('♯', '#').replace('♭', 'b');
  const sharpIdx = CHROMATIC_SHARP.indexOf(normalized as typeof CHROMATIC_SHARP[number]);
  const flatIdx  = CHROMATIC_FLAT.indexOf(normalized as typeof CHROMATIC_FLAT[number]);
  const idx = sharpIdx !== -1 ? sharpIdx : flatIdx;
  if (idx === -1) return note;

  const newIdx = ((idx + semitones) % 12 + 12) % 12;
  return (useSharps ?? false) ? CHROMATIC_SHARP[newIdx] : CHROMATIC_FLAT[newIdx];
}

export function noteToSemitone(note: string): number {
  const normalized = note.replace('♯', '#').replace('♭', 'b');
  const si = CHROMATIC_SHARP.indexOf(normalized as typeof CHROMATIC_SHARP[number]);
  if (si !== -1) return si;
  const fi = CHROMATIC_FLAT.indexOf(normalized as typeof CHROMATIC_FLAT[number]);
  return fi !== -1 ? fi : 0;
}

export function getRelativeKey(root: string, quality: ScaleQuality): { root: string; quality: ScaleQuality } {
  if (quality === 'major') {
    // Relative minor = degree 6 (9 semitones up, or 3 down)
    const minorRoot = transposeNote(root, 9, false);
    return { root: minorRoot, quality: 'minor' };
  } else {
    // Relative major = 3 semitones up (minor third up)
    const majorRoot = transposeNote(root, 3, SHARP_KEYS.has(transposeNote(root, 3, true)));
    return { root: majorRoot, quality: 'major' };
  }
}

// ── Mode-aware public API ─────────────────────────────────────────

export function getModeNotes(root: string, mode: Mode): string[] {
  const sharps = usesSharpsForMode(root, mode);
  return MODE_INTERVALS[mode].map(offset => transposeNote(root, offset, sharps));
}

export function getDiatonicChordsForMode(root: string, mode: Mode): DiatonicChord[] {
  const notes = getModeNotes(root, mode);
  const qualities = MODE_DIATONIC_QUALITIES[mode];
  const romans = MODE_ROMAN_NUMERALS[mode];
  const functions = MODE_CHORD_FUNCTIONS[mode];

  return notes.map((note, i) => {
    const chordQuality = qualities[i];
    const chord = buildChord(note, chordQuality);
    return {
      degree: (i + 1) as ChordDegree,
      romanNumeral: romans[i],
      chordFunction: functions[i],
      chordId: chord.id,
      rootNote: note,
      quality: chordQuality,
    };
  });
}

export function buildKeyForMode(root: string, mode: Mode): Key {
  const quality = MODE_TO_QUALITY[mode];
  const id = encodeKeyId(root, quality);
  const notes = getModeNotes(root, mode);
  // Per spec § 1.6: relative-key mechanics are a major/minor concept. For
  // non-Ionian/Aeolian modes, point at the relative of the *parent* major.
  const relativeKey = (mode === 'ionian' || mode === 'aeolian')
    ? getRelativeKey(root, quality)
    : getRelativeKey(parentMajorRoot(root, mode), 'major');
  const diatonicChords = getDiatonicChordsForMode(root, mode);

  // Display: Ionian/Aeolian use the friendly Major/Minor names always at this layer.
  // The Settings filter (useTraditionalNames) is applied at the UI render layer
  // via displayModeName(); buildKeyForMode produces the canonical name so consumers
  // can re-render when the toggle flips.
  let displayName: string;
  if (mode === 'ionian') displayName = `${root} Major`;
  else if (mode === 'aeolian') displayName = `${root} Minor`;
  else displayName = `${root} ${mode.charAt(0).toUpperCase()}${mode.slice(1)}`;

  return { id, root, quality, mode, displayName, notes, relativeKey, diatonicChords };
}

export function getDiatonicChordIdsForMode(key: { root: string; mode: Mode }): string[] {
  return getDiatonicChordsForMode(key.root, key.mode).map(dc => dc.chordId);
}

// ── Legacy wrappers ───────────────────────────────────────────────

export function getScaleNotes(root: string, quality: ScaleQuality): string[] {
  return getModeNotes(root, quality === 'major' ? 'ionian' : 'aeolian');
}

export function getDiatonicChords(root: string, quality: ScaleQuality): DiatonicChord[] {
  return getDiatonicChordsForMode(root, quality === 'major' ? 'ionian' : 'aeolian');
}

export function buildKey(root: string, quality: ScaleQuality): Key {
  return buildKeyForMode(root, quality === 'major' ? 'ionian' : 'aeolian');
}

// ── Other utilities (unchanged) ───────────────────────────────────

export function getChordVariantIds(chordId: string, baseQuality: ChordQuality): string[] {
  const variantQualities = CHORD_VARIANTS[baseQuality] ?? [];
  // Extract root from chord id — remove quality suffix
  const root = chordId.replace(
    /(maj7|maj9|m7|m9|m6|dim7|dim|13|7|9|6|5|sus2|sus4|add9|ø7|\+|m)$/u,
    ''
  );
  return variantQualities.map(q => buildChord(root, q).id);
}

export interface ResolvedStep {
  chordId: string;
  numeral: string;
  rootNote: string;
  isChromatic: boolean;
}

// Resolve a progression's steps into chords for a given key/mode.
export function resolveProgression(steps: ProgressionStep[], key: Key): ResolvedStep[] {
  const sharps = usesSharpsForMode(key.root, key.mode);
  return steps.map((step) => {
    if (step.kind === 'diatonic') {
      // degree is 1-based; diatonicChords is always length 7 from buildKeyForMode.
      const dc = key.diatonicChords[step.degree - 1];
      if (step.quality) {
        // Diatonic 7th/extension on the same scale-degree root — NOT a borrowed chord.
        const chord = buildChord(dc.rootNote, step.quality);
        return { chordId: chord.id, numeral: step.numeral ?? dc.romanNumeral, rootNote: dc.rootNote, isChromatic: false };
      }
      return { chordId: dc.chordId, numeral: dc.romanNumeral, rootNote: dc.rootNote, isChromatic: false };
    }
    const rootNote = transposeNote(key.root, step.rootOffset, sharps);
    const chord = buildChord(rootNote, step.quality);
    return { chordId: chord.id, numeral: step.numeral, rootNote, isChromatic: true };
  });
}

// Numeral string for a progression's steps, read directly from the mode tables
// (diatonic numerals from the key, chromatic numerals from the step). Cheaper than
// routing through resolveProgression when only the title is needed.
export function stepsToNumeralString(steps: ProgressionStep[], key: Key): string {
  return steps
    .map((step) =>
      step.kind === 'diatonic'
        ? step.numeral ?? key.diatonicChords[step.degree - 1].romanNumeral
        : step.numeral,
    )
    .join('–');
}

// Card title: explicit nickname, else the (mode-correct) numeral string.
export function getProgressionTitle(p: Progression, key: Key): string {
  return p.name ?? stepsToNumeralString(p.steps, key);
}

/**
 * Returns the 7 diatonic chord ids for a given key, in scale-degree order (1..7).
 * Uses the existing buildChord + diatonic-quality logic; chord ids reflect the
 * post-Task-2 "dim" naming.
 */
export function getDiatonicChordIds(key: { root: string; quality: ScaleQuality }): string[] {
  return getDiatonicChords(key.root, key.quality).map(dc => dc.chordId);
}

/**
 * Returns the key ids (e.g. "C-major", "A-minor") whose diatonic chord set
 * is a SUPERSET of the input chord ids. Empty input returns all 24 keys.
 */
export function keysContainingChords(chordIds: string[]): string[] {
  const allKeys: { root: string; quality: ScaleQuality }[] = [];
  for (const root of CHROMATIC_SHARP) {
    allKeys.push({ root, quality: 'major' });
    allKeys.push({ root, quality: 'minor' });
  }

  if (chordIds.length === 0) {
    return allKeys.map(k => encodeKeyId(k.root, k.quality));
  }

  const required = new Set(chordIds);

  return allKeys
    .filter(k => {
      const diatonic = new Set(getDiatonicChordIds(k));
      for (const c of required) if (!diatonic.has(c)) return false;
      return true;
    })
    .map(k => encodeKeyId(k.root, k.quality));
}

// keyId encode/decode helpers (centralised here and re-exported from utils/keyId.ts)
export function encodeKeyId(root: string, quality: ScaleQuality): string {
  const safeRoot = root.replace('#', 'sharp');
  return `${safeRoot}-${quality}`;
}

export function decodeKeyId(keyId: string): { root: string; quality: ScaleQuality } | null {
  const parts = keyId.split('-');
  if (parts.length < 2) return null;
  const quality = parts[parts.length - 1] as ScaleQuality;
  if (quality !== 'major' && quality !== 'minor') return null;
  const rawRoot = parts.slice(0, -1).join('-').replace('sharp', '#');
  return { root: rawRoot, quality };
}
